import type { Playlist, PlaylistSummary, SpotifyTrackInput, YearOverride } from './models.js'

/** Media type of the `PATCH /playlists/{id}` body (RFC 7396). */
export const MERGE_PATCH_MEDIA_TYPE = 'application/merge-patch+json'

/**
 * The members of a playlist a client may patch. Absent means "leave alone";
 * `null` is rejected, since neither is optional.
 */
export interface PlaylistMergePatch {
  /** Display name; the desktop editor renames in place. */
  name?: string | null
  tracksPerYear?: number | null
  /** Let thin years play with what they have; unfillable cards are skipped. */
  allowUnevenDistribution?: boolean
  /**
   * Curator-set years by Spotify track ID. Merges member-by-member, so one
   * track can be changed without resending the rest; `null` clears that
   * track's override and restores whatever resolution found.
   */
  yearOverrides?: Record<string, YearOverride | null>
  /** Curator-set titles by Spotify track ID; `null` restores Spotify's. */
  titleOverrides?: Record<string, string | null>
}

/**
 * Where a batch of tracks was read from. Recorded on the playlist so a client
 * can offer to pull from the same place again, and link back to it.
 */
export interface SpotifyPlaylistSource {
  spotifyPlaylistId: string
  name?: string
  ownerName?: string
  coverUrl?: string
}

/**
 * `POST /playlists` — creates a playlist. Tracks are optional: a curator may
 * start empty and import later, or import a Spotify playlist in one step.
 */
export interface CreatePlaylistRequest {
  name: string
  tracks?: SpotifyTrackInput[]
  source?: SpotifyPlaylistSource
}

/**
 * `POST /playlists/{id}/tracks` — adds tracks read from Spotify. Tracks the
 * playlist already holds keep their resolved year and every curator edit and
 * only refresh their Spotify metadata; new ones are queued for resolution.
 * Nothing is ever removed by an import.
 */
export interface AddTracksRequest {
  tracks: SpotifyTrackInput[]
  source?: SpotifyPlaylistSource
}

export interface AddTracksResponse {
  playlist: Playlist
  /** Tracks that were new to the playlist. */
  added: number
  /** Submitted tracks the playlist already held; their metadata was refreshed. */
  duplicates: number
}

export interface PlaylistResponse {
  playlist: Playlist
}

export interface PlaylistListResponse {
  playlists: PlaylistSummary[]
}

export type ApiErrorCode =
  | 'unauthorized'
  | 'invalid_request'
  | 'not_found'
  | 'method_not_allowed'
  | 'conflict'
  | 'precondition_failed'
  | 'unsupported_media_type'
  | 'internal_error'

export interface ApiErrorResponse {
  error: {
    code: ApiErrorCode
    message: string
  }
}

/** Payload of the asynchronous year-resolution invocation. */
export interface ResolveJob {
  userId: string
  playlistId: string
}

export const MAX_TRACKS_PER_PLAYLIST = 2000

/** Longest playlist name accepted; anything longer is a paste accident. */
export const MAX_PLAYLIST_NAME_LENGTH = 200

/** Longest curated track title accepted. */
export const MAX_TRACK_TITLE_LENGTH = 300
